import type { AvatarState } from '../types/anna';

/**
 * Anna's "life" layer: small, irregular behaviours on top of her state —
 * attention (gaze), blinks, sighs, perk-ups on state changes and reactions to touch.
 * Pure simulation; the renderer feeds it input and draws the resulting frame.
 */

export interface LifeFrame {
  /** Attention direction, roughly -1..1 */
  gazeX: number;
  gazeY: number;
  /** 0..1 — core briefly dims and draws in */
  blink: number;
  /** 0..1 — slow, deep breath */
  sigh: number;
  /** Springy impulse, negative = contract, positive = perk up */
  perk: number;
  /** 0..1 — pointer is close, Anna leans in */
  prox: number;
  /** 0..1 — shimmer burst after a touch */
  burst: number;
}

export interface LifeInput {
  t: number;
  dt: number;
  state: AvatarState;
  reduced: boolean;
  /** Overall expressiveness: 1 lively, < 1 calmer */
  amount: number;
  /** Pointer direction relative to Anna, -1..1 */
  pointer: { x: number; y: number };
  /** Seconds since the pointer last moved */
  pointerAge: number;
  /** 0..1 target closeness of the pointer */
  proximity: number;
}

interface Spring {
  x: number;
  v: number;
}

export interface LifeState {
  prevState: AvatarState;
  gx: Spring;
  gy: Spring;
  target: { x: number; y: number };
  nextGlance: number;
  blinkStart: number;
  nextBlink: number;
  doubleBlink: boolean;
  sighStart: number;
  nextSigh: number;
  perk: Spring;
  prox: number;
  burst: number;
  frame: LifeFrame;
}

export const NO_LIFE: LifeFrame = { gazeX: 0, gazeY: 0, blink: 0, sigh: 0, perk: 0, prox: 0, burst: 0 };

const AWAKE: ReadonlySet<AvatarState> = new Set(['rust', 'luistert', 'denkt', 'spreekt', 'werkt', 'meldt', 'bezorgd']);

const PERK: Partial<Record<AvatarState, number>> = {
  rust: 0.15,
  luistert: 1,
  spreekt: 0.45,
  meldt: 0.8,
  werkt: 0.25,
  denkt: -0.35,
  bezorgd: -0.55,
  nietStoren: -0.4,
  gestopt: -1,
};

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

function stepSpring(s: Spring, target: number, k: number, zeta: number, dt: number) {
  const c = 2 * zeta * Math.sqrt(k);
  const steps = Math.max(1, Math.ceil(dt / 0.008));
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    s.v += ((target - s.x) * k - s.v * c) * h;
    s.x += s.v * h;
  }
}

export function createLife(state: AvatarState): LifeState {
  return {
    prevState: state,
    gx: { x: 0, v: 0 },
    gy: { x: 0, v: 0 },
    target: { x: 0, y: 0 },
    nextGlance: rand(1.5, 3),
    blinkStart: -1,
    nextBlink: rand(1.8, 4),
    doubleBlink: false,
    sighStart: -1,
    nextSigh: rand(12, 22),
    perk: { x: 0, v: 0 },
    prox: 0,
    burst: 0,
    frame: { ...NO_LIFE },
  };
}

/** A touch on Anna's core: she perks up and shimmers. */
export function pokeLife(life: LifeState, amount = 1) {
  life.perk.v += 7 * amount;
  life.burst = 1;
}

function pickGlance(state: AvatarState): { x: number; y: number } {
  if (state === 'denkt') {
    // Thinking looks drift up and to the side
    return { x: (Math.random() < 0.5 ? -1 : 1) * rand(0.35, 0.8), y: -rand(0.3, 0.7) };
  }
  if (state === 'luistert' || state === 'spreekt') {
    // Engaged: stays with you, tiny wanders only
    return { x: rand(-0.18, 0.18), y: rand(-0.1, 0.14) };
  }
  if (Math.random() < 0.35) return { x: 0, y: 0 };
  const a = Math.random() * Math.PI * 2;
  const r = rand(0.35, 0.85);
  return { x: Math.cos(a) * r, y: Math.sin(a) * r * 0.7 };
}

export function stepLife(life: LifeState, i: LifeInput): LifeFrame {
  const f = life.frame;
  life.prox += (i.proximity - life.prox) * Math.min(1, i.dt * 4);

  if (i.reduced) {
    f.gazeX = 0;
    f.gazeY = 0;
    f.blink = 0;
    f.sigh = 0;
    f.perk = 0;
    f.burst = 0;
    f.prox = life.prox * 0.5;
    life.prevState = i.state;
    return f;
  }

  const amt = i.amount;
  const { t, dt, state } = i;
  const awake = AWAKE.has(state);

  // State change: a small bodily reaction before the new state settles
  if (state !== life.prevState) {
    life.perk.v += (PERK[state] ?? 0) * 5.5 * amt;
    life.prevState = state;
    life.nextGlance = t + rand(0.6, 1.4);
    if (!awake) life.blinkStart = -1;
  }

  // Attention
  let k = 70;
  let zeta = 0.92;
  if (!awake) {
    life.target.x = 0;
    life.target.y = 0;
    k = 10;
    zeta = 1;
  } else if (i.pointerAge < 2.5) {
    life.target.x = i.pointer.x * 0.9;
    life.target.y = i.pointer.y * 0.9;
    life.nextGlance = t + rand(1.8, 3.5);
    k = 24;
    zeta = 1;
  } else if (t >= life.nextGlance) {
    life.target = pickGlance(state);
    life.nextGlance = t + (state === 'denkt' ? rand(1.2, 2.6) : rand(2.6, 6.5)) / Math.max(0.5, amt);
  }
  stepSpring(life.gx, life.target.x * amt, k, zeta, dt);
  stepSpring(life.gy, life.target.y * amt, k, zeta, dt);
  // Fixational drift — attention is never perfectly still
  const drift = awake ? 0.025 * amt : 0;
  f.gazeX = life.gx.x + Math.sin(t * 1.7) * drift + Math.sin(t * 4.3) * drift * 0.4;
  f.gazeY = life.gy.x + Math.sin(t * 1.3 + 1) * drift;

  // Blinks
  if (awake && life.blinkStart < 0 && t >= life.nextBlink) {
    life.blinkStart = t;
    life.doubleBlink = Math.random() < 0.18;
    life.nextBlink = t + (state === 'denkt' ? rand(5, 9) : rand(3.2, 7)) / Math.max(0.6, amt);
  }
  let blink = 0;
  if (life.blinkStart >= 0) {
    const u = (t - life.blinkStart) / 0.2;
    if (u < 1) blink = Math.sin(Math.PI * u);
    else if (life.doubleBlink && u >= 1.25 && u < 2.25) blink = Math.sin(Math.PI * (u - 1.25));
    else if (u >= (life.doubleBlink ? 2.25 : 1)) life.blinkStart = -1;
  }
  f.blink = blink * amt;

  // Sighs while resting
  if (state === 'rust') {
    if (life.sighStart < 0 && t >= life.nextSigh) {
      life.sighStart = t;
      life.nextSigh = t + rand(18, 34);
    }
  } else {
    life.nextSigh = Math.max(life.nextSigh, t + 8);
  }
  let sigh = 0;
  if (life.sighStart >= 0) {
    const u = (t - life.sighStart) / 3.4;
    if (u >= 1) life.sighStart = -1;
    else sigh = Math.pow(Math.sin(Math.PI * u), 2);
  }
  f.sigh = sigh * amt;

  // Perk spring — underdamped so it wobbles once, like a living thing
  stepSpring(life.perk, 0, 38, 0.32, dt);
  f.perk = clamp(life.perk.x, -1.2, 1.2);

  life.burst = Math.max(0, life.burst - dt * 1.6);
  f.burst = life.burst;
  f.prox = awake ? life.prox * amt : 0;
  return f;
}

/** 0 by day, 1 deep at night — Anna warms and slows down in the evening. */
export function nightness(date = new Date()): number {
  const h = date.getHours() + date.getMinutes() / 60;
  if (h >= 23 || h < 5) return 1;
  if (h >= 19) return (h - 19) / 4;
  if (h < 8) return 1 - (h - 5) / 3;
  return 0;
}
